import { useEffect, useMemo, useState } from 'react';
import { Flex } from 'antd';
import { ActionTaken, type LBNodeData } from '@/proto/logiblocks/logiblocks';
import useToken from '@/common/helper/useToken';
import { listAlertTags, type AlertTag } from '@/apis/logiblocksAPI';
import { CustomShipmentText } from '@/components';
import CheckboxMultiSelect, {
  type CheckboxMultiSelectGroup,
} from '@/components/CheckboxMultiSelect';
import { CustomizationComponent } from '@/pages/Logiblocks/types';
import { AlertActionTakenTriggerCustomization } from './AlertActionTakenTriggerCustomization';

// The manual actions that carry a tag, and which of the company's tag lists each
// one picks from. AI and system actions never have a tag.
const TAG_CATEGORY_BY_ACTION: Partial<
  Record<ActionTaken, AlertTag['category']>
> = {
  [ActionTaken.ACTION_TAKEN_MANUAL_ASSIGNED]: 'assignment',
  [ActionTaken.ACTION_TAKEN_MANUAL_RESOLVED]: 'resolution',
  [ActionTaken.ACTION_TAKEN_MANUAL_DISMISSED]: 'dismissal',
};
const TAG_CATEGORY_LABELS: Record<AlertTag['category'], string> = {
  assignment: 'Assignment tags',
  resolution: 'Resolution tags',
  dismissal: 'Dismissal tags',
};

/** The tag categories the selected actions can carry, in menu order. */
export const tagCategoriesFor = (
  actionTaken: readonly ActionTaken[]
): AlertTag['category'][] => {
  const categories = new Set(
    actionTaken.flatMap((action): AlertTag['category'][] => {
      const category = TAG_CATEGORY_BY_ACTION[action];
      return category ? [category] : [];
    })
  );
  return (['assignment', 'resolution', 'dismissal'] as const).filter(
    (category) => categories.has(category)
  );
};

/**
 * The selected tags a set of actions can still carry. A tag of an action no longer
 * selected could never match, so it is dropped rather than left to filter unseen.
 * Before the tags load the selection is kept rather than guessed at.
 */
export const keepTagsFor = (
  actionTaken: readonly ActionTaken[],
  tagIds: readonly string[],
  alertTags: readonly AlertTag[] | null
): string[] => {
  const categories = new Set(tagCategoriesFor(actionTaken));
  if (categories.size === 0) return [];
  if (alertTags === null) return [...tagIds];
  const categoryById = new Map(alertTags.map((tag) => [tag.id, tag.category]));
  return tagIds.filter((id) => {
    const category = categoryById.get(id);
    return category === undefined || categories.has(category);
  });
};

/**
 * The Alert action taken trigger's editor plus its "With tag" filter: a manual action
 * only runs when taken with one of the chosen tags of its own kind (an assignment
 * tag, or a resolve/dismiss close tag). An action with none of its kind chosen runs
 * for any tag or none (backend: alert_action_tags.tag_filter_matches).
 */
export const AlertActionTakenTriggerWithTagFilter: CustomizationComponent = (
  props
) => {
  const { nodeData, onChange, isReadOnly = false, step } = props;
  const { getToken } = useToken();
  const [alertTags, setAlertTags] = useState<AlertTag[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    const token = getToken();
    if (!token) return;
    let cancelled = false;
    listAlertTags(token)
      .then((tags) => {
        if (!cancelled) setAlertTags(tags);
      })
      .catch((error: unknown) => {
        console.error('Error fetching alert tags:', error);
        if (!cancelled) setLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch once per mount
  }, []);

  const trigger = nodeData.alert_action_taken_trigger;
  const selectedActions = useMemo(
    () => trigger?.action_taken ?? [],
    [trigger?.action_taken]
  );
  const selectedTagIds = useMemo(
    () => trigger?.tag_ids ?? [],
    [trigger?.tag_ids]
  );
  const tagGroups: CheckboxMultiSelectGroup[] = useMemo(() => {
    const groups = tagCategoriesFor(selectedActions).map((category) => ({
      label: TAG_CATEGORY_LABELS[category],
      options: (alertTags ?? [])
        .filter((tag) => tag.category === category)
        .map((tag) => ({ value: tag.id, label: tag.label })),
    }));
    // A chosen tag that was since turned off or deleted no longer lists; name it
    // rather than show its id, so it can be cleared.
    if (alertTags !== null && groups.length > 0) {
      const listed = new Set(alertTags.map((tag) => tag.id));
      const removed = selectedTagIds.filter((id) => !listed.has(id));
      if (removed.length > 0) {
        groups.push({
          label: 'Removed tags',
          options: removed.map((id) => ({ value: id, label: 'Removed tag' })),
        });
      }
    }
    return groups;
  }, [selectedActions, selectedTagIds, alertTags]);

  // The trigger editor saves its whole data on every change; keep the tag filter in
  // step with the actions it saves.
  const handleChange = (update: Partial<LBNodeData>) => {
    const next = update.alert_action_taken_trigger;
    if (!next) {
      onChange(update);
      return;
    }
    onChange({
      ...update,
      alert_action_taken_trigger: {
        ...next,
        tag_ids: keepTagsFor(
          next.action_taken,
          next.tag_ids ?? selectedTagIds,
          alertTags
        ),
      },
    });
  };

  const handleTagIdsChange = (values: Array<string | number>) => {
    if (!trigger) return;
    onChange({
      alert_action_taken_trigger: { ...trigger, tag_ids: values.map(String) },
    });
  };

  return (
    <Flex vertical gap={16}>
      <AlertActionTakenTriggerCustomization
        {...props}
        onChange={handleChange}
      />
      {step === 2 && tagGroups.length > 0 && (
        <Flex vertical gap={0}>
          <CustomShipmentText
            text='With tag'
            fontSize='14px'
            marginBottom='4px'
            fontWeight={600}
          />
          <CheckboxMultiSelect
            groups={tagGroups}
            getPopupContainer={(node) => node.parentElement ?? document.body}
            value={selectedTagIds}
            onChange={handleTagIdsChange}
            disabled={isReadOnly}
            className='agentSelect agentSelect--darkText'
            placeholder='Any tag'
            border='0px'
            background='rgba(94, 94, 94, 1)'
            color='white'
            showTagCloseIcon
          />
          <CustomShipmentText
            text={
              loadFailed
                ? "Couldn't load the company's tags. Reopen the step to try again."
                : 'An action with none of its tags chosen runs for any tag or none.'
            }
            fontSize='12px'
            color='var(--text-secondary)'
          />
        </Flex>
      )}
    </Flex>
  );
};
